'use client';
import { SessionContext } from '@benzinga/session-context';
import styled from '@benzinga/themetron';
import { noop } from '@benzinga/utils';
import React from 'react';
import { dropdownDescriptionHelper } from '../SearchBar';
import { AutocompleteModule, ActionModule, SearchModule, TagModule, ClipboardModule, QueryScore } from './Modules';
import { SearchContext, SearchContextType } from '../context';
import { useScannerConfigs } from '@benzinga/scanner-manager-hooks';
import { usePermission } from '@benzinga/user-context';
import { ScannerConfigManager, ScannerConfig } from '@benzinga/scanner-config-manager';
import {
  SearchItem,
  ScannerModuleId,
  ScannerSearchItem,
  ScannerSearchItemData,
  ScannerSearchItemVersion,
  createScannerSearchItem,
  isLegacyScannerSearchItem,
  isScannerSearchItem,
} from '@benzinga/search-modules';
import { Scanner } from '@benzinga/themed-icons';
import { WidgetContext, WidgetId } from '@benzinga/widget-tools';

export interface ScannerSearchModule extends SearchModule<ScannerSearchItemData> {
  id: typeof ScannerModuleId;
  autocomplete: AutocompleteModule<ScannerSearchItemData>;
  tag: TagModule;
  action: ActionModule;
  clipboard: ClipboardModule;
}

export const createScannerAutocompleteModule = (
  scannerConfigManager: ScannerConfigManager,
  searchContext: SearchContextType,
  scannerPermission: boolean,
  widgetId?: WidgetId,
): AutocompleteModule<ScannerSearchItemData> => {
  return {
    dropdown: (item: SearchItem) => {
      if (!isScannerSearchItem(item)) return undefined;
      const scannerConfig = scannerConfigManager.getConfigs().find(s => s.uuid === item.data.scannerId);
      const scannerName = scannerConfig ? (scannerConfig?.name ?? 'UNNAMED SCANNER') : 'SCANNER NOT FOUND';
      return {
        component: dropdownDescriptionHelper('Scanner', scannerName),
        id: item.id,
        locked: !scannerPermission,
        searchItem: item,
        type: 'AutocompleteDropdown',
        version: ScannerSearchItemVersion,
      };
    },
    onSelect: (item: SearchItem) => {
      if (!isScannerSearchItem(item)) return undefined;
      return item;
    },
    query: (text: string, limit: number, selectedItems?: SearchItem[]) => {
      const lowercaseText = text.toLowerCase();
      const allScanners = scannerConfigManager.getConfigs();
      const filteredScanners = allScanners.filter(
        scan =>
          selectedItems == null ||
          !selectedItems.some(item => isScannerSearchItem(item) && item.data.scannerId === scan.uuid),
      );
      const scoredScanner = filteredScanners.flatMap<[ScannerConfig, QueryScore]>(scanner => {
        if (lowercaseText === '') {
          if (
            searchContext
              .getRecentModuleItem(widgetId)
              .some(item => isScannerSearchItem(item) && item.data.scannerId === scanner.uuid)
          )
            return [[scanner, QueryScore.HistoryMatch]];
          return [[scanner, QueryScore.NoMatch]];
        }
        if (scanner.name?.toLowerCase() === lowercaseText) {
          return [[scanner, QueryScore.FullMatch]];
        }
        if (scanner.name?.toLowerCase().startsWith(lowercaseText)) {
          return [[scanner, QueryScore.StartsWithMatch]];
        }
        if (scanner.name?.toLowerCase().includes(lowercaseText)) {
          return [[scanner, QueryScore.IncludesMatch]];
        }
        return [];
      });
      const sortedScanners = scoredScanner.sort(
        (scoredScanner1, scoredScanner2) => scoredScanner2[1] - scoredScanner1[1],
      );
      const result = sortedScanners
        .slice(undefined, limit)
        .map<
          [SearchItem<ScannerSearchItemData>, QueryScore]
        >(scoredScanner => [createScannerSearchItem(scoredScanner[0].uuid ?? ''), scoredScanner[1]]);
      return {
        cancel: noop,
        promise: new Promise(resolve => {
          resolve({ canceled: false, ok: result });
        }),
      };
    },
  };
};

const ScannerTag: React.FC<{ uuid: string }> = ({ uuid }) => {
  const scannerConfigs = useScannerConfigs();
  const name = React.useMemo(
    () => scannerConfigs?.find(s => s.uuid === uuid)?.name ?? 'UNNAMED SCANNER',
    [scannerConfigs, uuid],
  );
  return <>{name}</>;
};

export const createScannerTagModule = (scannerConfigManager: ScannerConfigManager): TagModule => {
  return {
    render: item => {
      if (!isScannerSearchItem(item)) return undefined;
      return {
        icon: <StyledScannerIcon />,
        text: <ScannerTag uuid={item.data.scannerId} />,
        type: 'text',
      };
    },
    tagInputText: item => {
      if (!isScannerSearchItem(item)) return undefined;
      const name = scannerConfigManager.getConfigs().find(s => s.uuid === item.data.scannerId)?.name;
      return name ?? '';
    },
  };
};

export const createScannerActionModule = (): ActionModule => {
  return {
    dropdown: () => {
      return [];
    },
  };
};

export const createScannerClipboardModule = (scannerPermission: boolean): ClipboardModule => {
  return {
    onCopyJSON: token => {
      if (!scannerPermission) return undefined;
      if (!isScannerSearchItem(token)) return undefined;
      return [token];
    },
    onPasteJSON: (token): ScannerSearchItem | undefined => {
      if (!scannerPermission) return undefined;
      if (isLegacyScannerSearchItem(token)) return ScannerModuleItemLoader(scannerPermission)(token);
      return undefined;
    },
  };
};

const ScannerModuleItemLoader = (scannerPermission: boolean) => (item: SearchItem) => {
  if (!scannerPermission) return undefined;
  if (!isLegacyScannerSearchItem(item)) return undefined;

  switch (item.version) {
    case ScannerSearchItemVersion:
    default:
      return item;
  }
};

export const createScannerSearchModule = (
  scannerConfigManager: ScannerConfigManager,
  searchContext: SearchContextType,
  scannerPermission = true,
  widgetId?: WidgetId,
): ScannerSearchModule => {
  return {
    action: createScannerActionModule(),
    autocomplete: createScannerAutocompleteModule(scannerConfigManager, searchContext, scannerPermission, widgetId),
    clipboard: createScannerClipboardModule(scannerPermission),
    id: ScannerModuleId,
    loader: ScannerModuleItemLoader(scannerPermission),
    tag: createScannerTagModule(scannerConfigManager),
  };
};

export const useScannerSearchModule = () => {
  const session = React.useContext(SessionContext);
  const search = React.useContext(SearchContext);
  const scannerConfigManager = session.getManager(ScannerConfigManager);
  const scannerPermission = usePermission('bzpro/widget/use', 'scanner');
  const widgetContext = React.useContext(WidgetContext);
  return React.useMemo(
    () => createScannerSearchModule(scannerConfigManager, search, scannerPermission, widgetContext.widgetId),
    [scannerConfigManager, scannerPermission, search, widgetContext.widgetId],
  );
};

const StyledScannerIcon = styled(Scanner)`
  vertical-align: middle;
  width: 1em;
  height: 1em;
  margin-right: 4px;
`;
